import * as React from 'react';
import { Breadcrumb as DefaultBreadcrumb, IBreadcrumbProps, IBreadcrumbItem } from 'office-ui-fabric-react/lib/Breadcrumb';
import { itemsBreadcrumbBetaControl, crumbControl } from '../../controls/itemsBreadcrumbBetaControl';
import { WithItemCache, IItemCacheStateProps } from '../withItemCache/WithItemCache';
import { Omit } from '../../utilities/Omit';
import { IItemCache } from '../../models/IItemCacheObserver';
import { parentFolderItemFacet, displayNameFacet } from '../../facets/ItemFacets';
import { ItemOpenAction } from '../actions/open/OpenAction';
import { WithActionSet, IWithActionSetStateProps } from '../withAction/WithAction';
import { withCurrentItemSet, getItemKey, IDecorateWithCurrentItemSetProps } from '../decorators/selectors';
import { WithItemsScope, IItemsScopeStateProps } from '../itemsScope/ItemsScope';
import { withDisplayName } from '../../utilities/components/Decorators';
import { TelemetryProvider } from '../telemetry/TelemetryProvider';
import { IEventContext } from '@ms/telemetry-manager/lib/IEvent';

export type IItemsBreadcrumbBetaBaseProps = Omit<IBreadcrumbProps, 'items'> & {
  itemKey: string;
};

export type IItemsBreadcrumbBetaProps = IDecorateWithCurrentItemSetProps<IItemsBreadcrumbBetaBaseProps>;

const BREADCRUMB_TELEMETRY_CONTEXT: IEventContext = {
  component: 'Breadcrumb'
};

/**
 * Beta version of ItemsBreadcrumb which uses a cleaner control handler pattern
 * in order to provide flexibility of customization for crumbs and the entire breadcrumb layout.
 */
export const ItemsBreadcrumbBeta: React.ComponentType<IItemsBreadcrumbBetaProps> =
  withDisplayName('ItemsBreadcrumbBeta')(withCurrentItemSet()(
    withDisplayName('ItemsBreadcrumbBeta.Inner')(class extends React.PureComponent<IItemsBreadcrumbBetaBaseProps> {
      public render(): JSX.Element {
        const {
          itemKey: itemSetKey,
          ...breadcrumbProps
        } = this.props;

        return (
          <WithItemsScope>
            { ({ itemsScopeItemKey }: IItemsScopeStateProps) => {
              return (
                <WithItemCache>
                  { ({ itemCache }: IItemCacheStateProps) => {
                    const itemKey = getItemKey(itemCache, {
                      itemKey: itemSetKey
                    }) || itemSetKey;

                    const controlHandler = itemCache.demandItemFacet(itemsBreadcrumbBetaControl, itemKey);

                    const {
                      leafItemKey,
                      crumbControlHandler,
                      breadcrumbAs: Breadcrumb
                    } = itemsBreadcrumbBetaControl.evaluate(controlHandler)(itemCache, {
                      itemKey: itemSetKey,
                      itemsScopeItemKey: itemsScopeItemKey,
                      leafItemKey: itemKey,
                      crumbControlHandler: crumbControl.default,
                      breadcrumbAs: DefaultBreadcrumb
                    });

                    const actions: {
                      [itemKey: string]: JSX.Element | null;
                    } = {};

                    const displayNames: {
                      [itemKey: string]: string | undefined;
                    } = {};

                    const itemKeys: string[] = [];

                    let currentItemKey: string | undefined = leafItemKey;

                    while (currentItemKey && !(currentItemKey in actions)) {
                      const {
                        displayName,
                        parentItemKey,
                        action
                      } = crumbControl.evaluate(crumbControlHandler)(itemCache, {
                        itemKey: currentItemKey,
                        displayName: getDisplayName(itemCache, { itemKey: currentItemKey }),
                        parentItemKey: getParentItemKey(itemCache, { itemKey: currentItemKey }),
                        action: getAction(itemCache, { itemKey: currentItemKey })
                      });

                      displayNames[currentItemKey] = displayName;
                      actions[currentItemKey] = action;

                      itemKeys.unshift(currentItemKey);

                      currentItemKey = parentItemKey;
                    }

                    return (
                      <TelemetryProvider
                        withContext={ BREADCRUMB_TELEMETRY_CONTEXT }
                      >
                        <WithActionSet
                          actions={ actions }
                        >
                          { ({ actions: actionStates }: IWithActionSetStateProps) => {
                            const items = itemKeys.map((crumbItemKey: string): IBreadcrumbItem => {
                              const actionState = actionStates[crumbItemKey];

                              const onClick = actionState && actionState.isAvailable ? () => {
                                actionState.execute({});
                              } : undefined;

                              return {
                                text: displayNames[crumbItemKey] || '\u200B',
                                key: crumbItemKey,
                                onClick: onClick,
                                isCurrentItem: actionState && !!actionState.isToggled || false
                              };
                            });

                            return (
                              <Breadcrumb
                                { ...breadcrumbProps }
                                items={ items }
                              />
                            );
                          } }
                        </WithActionSet>
                      </TelemetryProvider>
                    );
                  } }
                </WithItemCache>
              );
            } }
          </WithItemsScope>
        );
      }
    })
  ));

function getParentItemKey(itemCache: IItemCache, props: {
  itemKey: string;
}): string | undefined {
  const parentItem = itemCache.demandItemFacet(parentFolderItemFacet, getItemKey(itemCache, {
    itemKey: props.itemKey
  }));

  return parentItem && parentItem.itemKey;
}

function getDisplayName(itemCache: IItemCache, props: {
  itemKey: string;
}): string | undefined {
  return itemCache.demandItemFacet(displayNameFacet, getItemKey(itemCache, {
    itemKey: props.itemKey
  }));
}

function getAction(itemCache: IItemCache, props: {
  itemKey: string;
}): JSX.Element {
  return (
    <ItemOpenAction itemKey={ props.itemKey } />
  );
}
